/**
 * Layer 3 of the anchor: the two integrity hashes. SPEC §3.1.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A FROZEN CONTRACT. See the banner in `types.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Storing TWO hashes is what turns the design's worst blind spot into its most
 * useful signal (SPEC §3.1). With only a folded hash, a Tanzil *harakat*
 * revision — and Tanzil explicitly publishes revisions — is invisible: same
 * folded hash, same word count, tier 1, no signal at all. With both:
 *
 *   raw match    + fold match     → nothing changed. Silent.
 *   raw mismatch + fold match     → the diacritics were corrected; the span is
 *                                   still the right words. Quiet marker.
 *   raw mismatch + fold mismatch  → real drift. Run the ladder.
 *
 * Both hashes are `sha256(...)` rendered as lowercase hex and truncated to the
 * first 16 characters — 64 bits. That is a collision-detection budget, not a
 * security budget: the adversary is a text revision upstream, not a forger, and
 * the cost of a collision is one note that fails to flag rather than one note
 * that is lost. Storing 16 bytes per note instead of 64 across a few thousand
 * notes is the trade the spec takes.
 *
 * WebCrypto only. No dependency, no bundled sha256, no synchronous variant —
 * `crypto.subtle.digest` is async everywhere it exists, and a second
 * implementation is a second thing that can disagree with the first.
 */

import { fold } from './segment';

/** Hex characters kept from the full sha256. SPEC §3.1: `.slice(0,16)`. */
export const HASH_PREFIX_LENGTH = 16;

/** A 16-character lowercase-hex value that is definitively not a real hash. */
export const EMPTY_HASH = '';

const encoder = new TextEncoder();

function toHex(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let out = '';
	for (let i = 0; i < bytes.length; i++) {
		out += (bytes[i] as number).toString(16).padStart(2, '0');
	}
	return out;
}

/** Full lowercase-hex sha256 of the UTF-8 encoding of `input`. */
export async function sha256Hex(input: string): Promise<string> {
	const digest = await crypto.subtle.digest('SHA-256', encoder.encode(input));
	return toHex(digest);
}

/** `sha256(input)` truncated to `HASH_PREFIX_LENGTH` hex characters. */
export async function textHash(input: string): Promise<string> {
	return (await sha256Hex(input)).slice(0, HASH_PREFIX_LENGTH);
}

/**
 * Both integrity hashes for one canonical span.
 *
 * `span` must already be the canonical span — the output of
 * `spanText(segment(text).slice(start, end))` — so that the hash does not
 * depend on how the source file happened to space the words.
 */
export async function anchorHashes(
	span: string
): Promise<{ text_hash_raw: string; text_hash_fold: string }> {
	const [raw, folded] = await Promise.all([textHash(span), textHash(fold(span))]);
	return { text_hash_raw: raw, text_hash_fold: folded };
}

/**
 * Constant-shape hash comparison. `undefined`/`''` on either side is NOT a
 * match — an anchor minted before Layer 3 existed, or one whose hash was lost
 * in a bad import, must not be reported as verified.
 */
export function hashMatches(stored: string | undefined, current: string | undefined): boolean {
	if (!stored || !current) return false;
	return stored === current;
}
